import React from 'react';
import { Card } from '$/components/Card';

import {
  BillingCycle,
  ProductKind,
  ProductType,
  SubscriptionSubstatus,
  TeamForAdminFragment,
} from '$/GraphQL/schema/types';
import EDate from '$/shared/frontend/utils/EDate';

import { formatCurrency } from '$/utils/formatCurrency';

import { usePlanPageContext } from './PlanPage';
import { css } from '@emotion/react';
import { theme } from 'twin.macro';
import { PlanPageModals, usePlanPageCTAButtons } from './PlanPageCTAButtons';
import { SubscriptionInfo } from './SubscriptionInfo';
import { smoothScroll } from '$/utils/scroll';
import { formatNumber, formatShortNumber } from '$/utils/number';

class SubscriptionInfoCard {
  public constructor(
    protected team: TeamForAdminFragment,
    protected subscriptionInfo: SubscriptionInfo
  ) {}

  public colors = {
    red: theme`colors.plan.red`.toString(),
    green: theme`colors.plan.green`.toString(),
    blue: theme`colors.plan.blue`.toString(),
    black: theme`colors.plan.black`.toString(),
  };

  public get title(): string | React.ReactElement {
    return 'Title';
  }

  public get content(): string | React.ReactElement {
    return 'Content';
  }

  public get subContent(): string | React.ReactElement | null {
    return null;
  }

  public get ctaButton(): string | React.ReactElement | null {
    return null;
  }

  public get color(): string | undefined {
    return undefined;
  }

  public get subColor(): string | undefined {
    return undefined;
  }

  public get note(): null {
    return null;
  }

  public formatNumber(amount: number): string {
    return formatNumber(amount);
  }

  public render(): React.ReactElement {
    return (
      <Card
        className='mx-2 mb-4'
        css={css`
          width: calc(50vw - 1.5rem);
          @media only screen and (min-width: 992px) {
            width: 204px;
          }
        `}
      >
        <Card.Title>{this.title}</Card.Title>
        <Card.Content color={this.color}>{this.content}</Card.Content>
        <Card.SubContent color={this.subColor}>
          {this.subContent ?? (this.ctaButton ? null : <span>&nbsp;</span>)}
        </Card.SubContent>
        {this.ctaButton ?? null}
      </Card>
    );
  }
}

class SubscriptionStatusInfo extends SubscriptionInfoCard {
  public get status(): string {
    switch (this.subscriptionInfo.subStatus) {
      case SubscriptionSubstatus.Active:
        return 'Active';
      case SubscriptionSubstatus.ActiveCanceled:
      case SubscriptionSubstatus.ChurnedVoluntary:
      case SubscriptionSubstatus.TrialingCanceled:
        return 'Canceled';
      case SubscriptionSubstatus.ChurnedDelinquent:
      case SubscriptionSubstatus.ChurnedTrial:
      case SubscriptionSubstatus.NoSubscription:
      case SubscriptionSubstatus.Paused:
        return 'Suspended';
      case SubscriptionSubstatus.Pending:
        return 'Pending Payment';
      case SubscriptionSubstatus.PastDue:
        return 'Past due';
      case SubscriptionSubstatus.Trialing:
        return 'Trialing';
    }
  }

  public get color(): string {
    let color = this.colors.black;
    switch (this.subscriptionInfo.subStatus) {
      case SubscriptionSubstatus.ActiveCanceled:
      case SubscriptionSubstatus.ChurnedVoluntary:
      case SubscriptionSubstatus.TrialingCanceled:
      case SubscriptionSubstatus.ChurnedDelinquent:
      case SubscriptionSubstatus.ChurnedTrial:
      case SubscriptionSubstatus.NoSubscription:
      case SubscriptionSubstatus.PastDue:
        color = this.colors.red;
    }
    return color;
  }

  public get title(): string | React.ReactElement {
    const eWebinarProduct = this.subscriptionInfo.products.find(
      ({ kind }) => kind === ProductKind.Webinars
    );
    if (eWebinarProduct?.currentPriceTier?.name) {
      return `${eWebinarProduct?.currentPriceTier?.name} subscription`;
    }
    return 'Subscription status';
  }

  public get CTAButton(): React.FC {
    const CTAButton: React.FC = () => {
      const { toggleModal } = usePlanPageCTAButtons();
      if (this.subscriptionInfo.subStatus !== SubscriptionSubstatus.PastDue) return null;

      return (
        <Card.CTA
          className='whitespace-no-wrap'
          onClick={(): void => toggleModal(PlanPageModals.UpdatePaymentDetails)}
        >
          Update payment details now
        </Card.CTA>
      );
    };
    return CTAButton;
  }

  public get content(): string | React.ReactElement {
    return this.status;
  }

  public get subContent(): string | React.ReactElement | null {
    const addOns = this.subscriptionInfo.products.filter(
      ({ kind, type }) => kind === ProductKind.AddOn && type !== ProductType.AdditionalRegistrants
    );
    if (addOns.length === 0) return null;
    return `+ ${addOns.length} subscription add-ons`;
  }

  public get ctaButton(): string | React.ReactElement | null {
    if (this.subscriptionInfo.subStatus !== SubscriptionSubstatus.PastDue) return null;
    return <this.CTAButton />;
  }
}

class SubscriptionAmountInfo extends SubscriptionInfoCard {
  public get title(): string | React.ReactElement {
    switch (this.subscriptionInfo.subStatus) {
      case SubscriptionSubstatus.Active:
      case SubscriptionSubstatus.ActiveCanceled:
      case SubscriptionSubstatus.ChurnedVoluntary:
      case SubscriptionSubstatus.TrialingCanceled:
      case SubscriptionSubstatus.Trialing:
        return 'Subscription rate';
      case SubscriptionSubstatus.ChurnedDelinquent:
      case SubscriptionSubstatus.ChurnedTrial:
      case SubscriptionSubstatus.NoSubscription:
      case SubscriptionSubstatus.PastDue:
      case SubscriptionSubstatus.Pending:
      case SubscriptionSubstatus.Paused:
        return 'Amount due';
    }
  }

  public get amount(): number {
    return this.subscriptionInfo.products.reduce((amount, product) => {
      return amount + (product.price ?? 0);
    }, 0);
  }

  public get subColor(): string | undefined {
    return this.colors.blue;
  }

  public get subContent(): string | React.ReactElement | null {
    if (
      !this.subscriptionInfo.credit ||
      this.subscriptionInfo.subStatus !== SubscriptionSubstatus.Active
    )
      return null;
    const credit = Math.min(this.subscriptionInfo.credit, this.amount);
    return `${formatCurrency(
      credit,
      this.subscriptionInfo.currency
    )} credit applied to next invoice`;
  }

  public get content(): string | React.ReactElement {
    switch (this.subscriptionInfo.subStatus) {
      case SubscriptionSubstatus.Trialing:
        return 'Free';
      case SubscriptionSubstatus.ActiveCanceled:
      case SubscriptionSubstatus.ChurnedVoluntary:
      case SubscriptionSubstatus.TrialingCanceled:
      case SubscriptionSubstatus.Paused:
      case SubscriptionSubstatus.Pending:
        return 'N/A';
      case SubscriptionSubstatus.Active:
        return (
          <span>
            {formatCurrency(this.amount, this.subscriptionInfo.currency)}
            <span className='text-xs'>
              /{this.team.billingCycle === BillingCycle.Month ? 'month' : 'year'}
            </span>
          </span>
        );
      case SubscriptionSubstatus.ChurnedDelinquent:
      case SubscriptionSubstatus.ChurnedTrial:
      case SubscriptionSubstatus.NoSubscription:
      case SubscriptionSubstatus.PastDue:
        return formatCurrency(
          this.subscriptionInfo.amountOwed ?? 0,
          this.subscriptionInfo.currency
        );
    }
  }
}

class NextBillingDateInfo extends SubscriptionInfoCard {
  public get title(): string | React.ReactElement {
    switch (this.subscriptionInfo.subStatus) {
      case SubscriptionSubstatus.ActiveCanceled:
      case SubscriptionSubstatus.TrialingCanceled:
      case SubscriptionSubstatus.ChurnedDelinquent:
      case SubscriptionSubstatus.ChurnedTrial:
      case SubscriptionSubstatus.ChurnedVoluntary:
        return 'Subscription end date';
      case SubscriptionSubstatus.PastDue:
        return 'Please pay by';
      case SubscriptionSubstatus.Trialing:
        return 'Trial end date';
    }
    return 'Next billing date';
  }

  public get date(): EDate | null | undefined {
    switch (this.subscriptionInfo.subStatus) {
      case SubscriptionSubstatus.ChurnedDelinquent:
      case SubscriptionSubstatus.ChurnedTrial:
      case SubscriptionSubstatus.ChurnedVoluntary:
      case SubscriptionSubstatus.NoSubscription:
      case SubscriptionSubstatus.ActiveCanceled:
      case SubscriptionSubstatus.TrialingCanceled:
        return this.subscriptionInfo.endedDate;
      case SubscriptionSubstatus.Trialing:
        return this.subscriptionInfo.trialEndingDate;
      default:
        return this.subscriptionInfo.nextPaymentDueDate;
    }
  }

  public get subContent(): string | null {
    switch (this.subscriptionInfo.subStatus) {
      case SubscriptionSubstatus.Trialing:
        const leftDays = this.date?.formatDistanceToNow({
          unit: 'day',
        });
        return `Your free trial ends in ${leftDays}`;
      case SubscriptionSubstatus.PastDue:
        return 'Your account will be suspended';
    }
    return null;
  }

  public get color(): string | undefined {
    return this.subscriptionInfo.subStatus === SubscriptionSubstatus.PastDue
      ? this.colors.red
      : this.colors.black;
  }

  public get content(): string {
    if (!this.date) return '';
    return this.date.format('P');
  }
}

class ActiveWebinarLimitInfo extends SubscriptionInfoCard {
  public get content(): string | React.ReactElement {
    return `${this.subscriptionInfo.activeWebinarsLimit}`;
  }

  public get subContent(): string | React.ReactElement | null {
    const currentPriceTier = this.subscriptionInfo.product.currentPriceTier;
    if (!currentPriceTier || this.subscriptionInfo.product.additional <= 0) return null;
    return `${currentPriceTier?.to} included + ${this.subscriptionInfo.product.additional} additional`;
  }

  public get title(): string | React.ReactElement {
    return 'Active webinar limit';
  }
}

class ActiveWebinarInfo extends SubscriptionInfoCard {
  public get title(): string | React.ReactElement {
    return 'Active webinars';
  }

  public get content(): string | React.ReactElement {
    return `${this.subscriptionInfo.activeWebinars}`;
  }
}

class UnusedWebinarInfo extends SubscriptionInfoCard {
  public get title(): string | React.ReactElement {
    return 'Unused webinars';
  }

  public get content(): string | React.ReactElement {
    return `${this.subscriptionInfo.unusedWebinars}`;
  }
}

class RegistrantLimitInfo extends SubscriptionInfoCard {
  public get title(): string | React.ReactElement {
    return 'Registrant limit *';
  }

  public get content(): string | React.ReactElement {
    const { limit } = this.team.registrantLimit;

    return `${this.formatNumber(limit)}`;
  }

  protected formatShortNumber(amount: number): string {
    return formatShortNumber(amount);
  }

  public get subColor(): string | undefined {
    if (this.team.registrantLimit.isTrialing) {
      return this.colors.red;
    }
    return super.subColor;
  }

  public get subContent(): string | React.ReactElement | null {
    const { additionalLimit, limit, includedLimit, purchasedAdditionalLimit, isTrialing } =
      this.team.registrantLimit;

    if (isTrialing) {
      return `Free trial limit is ${this.formatShortNumber(limit)}`;
    }
    if (!purchasedAdditionalLimit) return null;

    return `${this.formatShortNumber(includedLimit)} included + ${this.formatShortNumber(
      purchasedAdditionalLimit + additionalLimit
    )} additional`;
  }
}

class RegistrantsInfo extends SubscriptionInfoCard {
  public get title(): string | React.ReactElement {
    return 'Registrants *';
  }

  public get content(): string {
    const { registrants } = this.team.registrantLimit;

    return this.formatNumber(registrants);
  }

  public get subContent(): string | React.ReactElement | null {
    if (!this.team.registrantLimit.resetAt) return null;
    return `Resets ${new EDate(this.team.registrantLimit.resetAt, 'UTC').format('P')}`;
  }
}

class RegistrantsLeftInfo extends RegistrantLimitInfo {
  public get title(): string | React.ReactElement {
    return '% Registrants left *';
  }

  public get color(): string | undefined {
    const { registrantsLeftPercent } = this.team.registrantLimit;

    if (registrantsLeftPercent < 20) return this.colors.red;
    return this.colors.black;
  }

  public get content(): string | React.ReactElement {
    const { registrantsLeftPercent } = this.team.registrantLimit;

    return `${this.formatNumber(registrantsLeftPercent)}%`;
  }

  public get subContent(): string | React.ReactElement | null {
    return null;
  }

  public get CTAButton(): React.FC {
    const CTAButton: React.FC = () => {
      const scrollToHash = React.useCallback(async (hash: string): Promise<void> => {
        try {
          const ele = document.getElementById(hash.substr(1));
          if (ele) {
            await smoothScroll(ele, 0);
          }
        } catch {}
      }, []);

      return (
        <Card.CTA onClick={async (): Promise<void> => await scrollToHash('#subscriptions')}>
          Add registrants
        </Card.CTA>
      );
    };
    return CTAButton;
  }

  public get ctaButton(): string | React.ReactElement | null {
    if (this.subscriptionInfo.isInTrial) return null;
    return <this.CTAButton />;
  }
}

const getAllSubscriptionInfo = (
  team: TeamForAdminFragment,
  subscriptionInfo: SubscriptionInfo
): SubscriptionInfoCard[] => {
  return [
    SubscriptionStatusInfo,
    SubscriptionAmountInfo,
    NextBillingDateInfo,
    ActiveWebinarLimitInfo,
    ActiveWebinarInfo,
    UnusedWebinarInfo,
    RegistrantLimitInfo,
    RegistrantsInfo,
    RegistrantsLeftInfo,
  ].map((Info) => new Info(team, subscriptionInfo));
};

export const SubscriptionInfoCards: React.FC = () => {
  const { team, subscriptionInfo } = usePlanPageContext();
  const infoCards = React.useMemo(
    () => getAllSubscriptionInfo(team, subscriptionInfo),
    [subscriptionInfo, team]
  );

  return (
    <section
      id='summary'
      className='flex flex-row flex-wrap mb-2'
      css={css`
        margin-top: 0.5rem;
        max-width: initial !important;
        @media only screen and (min-width: 992px) {
          margin-left: -0.5rem;
          margin-right: -0.5rem;
        }
      `}
    >
      {infoCards.map((card, idx) => (
        <div key={`card-info-${idx}`}>{card.render()}</div>
      ))}
      <div className='flex justify-center w-full text-gray-600 mb-1e'>* This month</div>
    </section>
  );
};
